import { Badge, Button, SettingsRow, SettingsSection, SettingsValue } from '@wundo/ui';
import type { UserRecord } from '@/lib/users.ts';
import { formatDateTime, formatNotificationTime } from '@/utils/dateUtils.ts';
import { languageOptions } from '@/utils/language.ts';
import {
  CHANNEL_LABELS,
  NOTIFICATION_CHANNEL_LABELS,
  channelTone,
  inactiveReasonLabel,
  notificationChannelTone,
  roleTone,
  statusTone,
  titleCase,
} from './userLabels.ts';

export interface UserInfoTabProps {
  user: UserRecord;
  /** Puts this person back on the mailing list. Only the last row uses it. */
  onResubscribe: () => void;
  resubscribing: boolean;
}

function languageName(code: string): string {
  return languageOptions.find((option) => option.value === code)?.label ?? code;
}

/** The record itself: everything `/users/:id` answers, read rather than edited. */
export function UserInfoTab({ user, onResubscribe, resubscribing }: UserInfoTabProps) {
  return (
    <SettingsSection title='User Information' className='mb-0'>
      <SettingsRow label='Name'>
        <SettingsValue>{`${user.first_name} ${user.last_name}`}</SettingsValue>
      </SettingsRow>
      <SettingsRow label='Email'>
        <SettingsValue>{user.email}</SettingsValue>
      </SettingsRow>
      <SettingsRow label='Phone'>
        <SettingsValue>{user.phone}</SettingsValue>
      </SettingsRow>
      <SettingsRow label='Language'>
        <SettingsValue>{languageName(user.language)}</SettingsValue>
      </SettingsRow>
      <SettingsRow label='Role'>
        <Badge tone={roleTone(user.role)}>{titleCase(user.role)}</Badge>
      </SettingsRow>
      <SettingsRow label='Channel'>
        <Badge tone={channelTone(user.channel)}>
          {CHANNEL_LABELS[user.channel] ?? 'Web'}
        </Badge>
      </SettingsRow>
      <SettingsRow label='Notification Channel'>
        <Badge tone={notificationChannelTone(user.notification_channel)}>
          {NOTIFICATION_CHANNEL_LABELS[user.notification_channel]}
        </Badge>
      </SettingsRow>
      <SettingsRow label='Status'>
        <span className='flex items-center gap-2'>
          <Badge tone={statusTone(user.status)}>{titleCase(user.status)}</Badge>
          {user.status === 'inactive' && user.inactive_reason && (
            <span className='text-xs text-ink-muted'>
              {inactiveReasonLabel(user.inactive_reason)}
            </span>
          )}
        </span>
      </SettingsRow>
      <SettingsRow label='Excluded from Metrics'>
        {user.excluded_from_metrics ? (
          <Badge tone='warning'>Yes</Badge>
        ) : (
          <SettingsValue>No</SettingsValue>
        )}
      </SettingsRow>
      <SettingsRow label='Timezone'>
        <SettingsValue>{user.timezone}</SettingsValue>
      </SettingsRow>
      <SettingsRow label='Notification Time'>
        <SettingsValue>{formatNotificationTime(user.notification_time)}</SettingsValue>
      </SettingsRow>
      {/* The only way back onto the list; see resubscribeToPlatformEmail. */}
      <SettingsRow label='Platform Email'>
        {user.email_opt_out_at ? (
          <span className='flex flex-wrap items-center gap-2'>
            <Badge tone='warning'>Unsubscribed</Badge>
            <span className='text-xs text-ink-muted'>
              {formatDateTime(user.email_opt_out_at)}
            </span>
            <Button
              variant='quiet'
              size='sm'
              disabled={resubscribing}
              loading={resubscribing}
              onClick={onResubscribe}
            >
              Resubscribe
            </Button>
          </span>
        ) : (
          <Badge tone='success'>Subscribed</Badge>
        )}
      </SettingsRow>
    </SettingsSection>
  );
}
